/**
 * Path Display Component - JavaScript Version
 * 
 * A reusable component for displaying calculated paths.
 * Shows path steps, cost, algorithm info, and execution details.
 */

import React, { useState } from 'react';
import './PathDisplay.css';

const PathDisplay = ({
  path,
  cost,
  algorithm,
  executionTime,
  nodes,
  showDetails = true,
  className = '',
  steps,
  currentStep = 0,
  totalSteps = 0,
  onStepChange,
  showStepVisualization = false,
  onNodeHighlight,
  onEdgeHighlight
}) => {
  // State for view mode
  const [viewMode, setViewMode] = useState('path');
  
  // Helper function to get node label
  const getNodeLabel = (nodeId) => {
    if (nodeId === undefined || nodeId === null) {
      return 'Unknown';
    }
    const node = nodes.find(n => n.id === nodeId);
    return node ? node.label : nodeId.toString();
  };

  // Helper function to format distance values
  const formatDistance = (distance) => {
    if (distance === undefined || distance === null) return '∞';
    if (distance === Infinity) return '∞';
    if (typeof distance === 'string' && distance === 'Infinity') return '∞';
    return distance.toString();
  };

  // Check if step visualization should be shown
  const hasStepData = steps && steps.length > 0 && showStepVisualization;

  // Create path steps for display
  const pathSteps = path.map((nodeId, index) => ({
    step: index + 1,
    nodeId,
    nodeLabel: getNodeLabel(nodeId),
    isStart: index === 0,
    isEnd: index === path.length - 1
  }));

  // Format execution time
  const formatExecutionTime = (time) => {
    if (time < 1) {
      return `${(time * 1000).toFixed(2)}ms`;
    }
    return `${time.toFixed(2)}s`;
  };

  // Format cost
  const formatCost = (cost) => {
    if (typeof cost === 'string') {
      return cost;
    }
    if (cost === -1) {
      return 'No path found';
    }
    return cost.toString();
  };

  // Format algorithm name
  const formatAlgorithmName = (algorithm) => {
    return algorithm.charAt(0).toUpperCase() + algorithm.slice(1) + ' Algorithm';
  };

  if (path.length === 0) {
    return (
      <div className={`path-display empty ${className}`}>
        <div className="empty-message">
          <span className="empty-icon">🚫</span>
          No path calculated
        </div>
      </div>
    );
  }

  return (
    <div className={`path-display ${className}`}>
      <div className="path-header">
        <h3 className="path-title">Calculated Path</h3>
        {showDetails && (
          <div className="path-meta">
            <span className="path-cost">
              <strong>Cost:</strong> {formatCost(cost)}
            </span>
            <span className="path-algorithm">
              <strong>Algorithm:</strong> {formatAlgorithmName(algorithm)}
            </span>
            <span className="path-time">
              <strong>Time:</strong> {formatExecutionTime(executionTime)}
            </span>
          </div>
        )}
      </div>

        {/* Mode toggle - only show if step data is available */}
        {hasStepData && (
          <div className="path-mode-toggle">
            <button 
              className={`mode-btn ${viewMode === 'path' ? 'active' : ''}`}
              onClick={() => setViewMode('path')}
            >
              Path View
            </button>
            <button 
              className={`mode-btn ${viewMode === 'steps' ? 'active' : ''}`}
              onClick={() => setViewMode('steps')}
            >
              Step-by-Step
            </button>
          </div>
        )}

      {/* Path View (existing functionality) */}
      {viewMode === 'path' && (
        <div className="path-content">
          <div className="path-steps">
            {pathSteps.map((step, index) => (
              <div
                key={step.nodeId}
                className={`path-step ${
                  step.isStart ? 'start' : step.isEnd ? 'end' : ''
                }`}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-node">
                  <span className="node-id">{step.nodeLabel}</span>
                  {step.isStart && <span className="step-label start-label">START</span>}
                  {step.isEnd && <span className="step-label end-label">END</span>}
                </div>
                {index < pathSteps.length - 1 && (
                  <div className="step-arrow">→</div>
                )}
              </div>
            ))}
          </div>

          {showDetails && (
            <div className="path-summary">
              <div className="summary-item">
                <span className="summary-label">Total Steps:</span>
                <span className="summary-value">{path.length}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Path Length:</span>
                <span className="summary-value">{path.length - 1} edges</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Cost:</span>
                <span className="summary-value">{formatCost(cost)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step Visualization (new functionality) */}
      {viewMode === 'steps' && hasStepData && (
        <StepVisualizationContent
          steps={steps}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onStepChange={onStepChange}
          onNodeHighlight={onNodeHighlight}
          onEdgeHighlight={onEdgeHighlight}
          nodes={nodes}
          getNodeLabel={getNodeLabel}
          formatDistance={formatDistance}
        />
      )}
    </div>
  );
};

// Step Visualization Content Component
const StepVisualizationContent = ({ 
  steps, 
  currentStep, 
  totalSteps, 
  onStepChange, 
  onNodeHighlight, 
  onEdgeHighlight, 
  nodes, 
  getNodeLabel, 
  formatDistance 
}) => {
  const currentStepData = steps[currentStep];
  
  if (!currentStepData) return null;
  
  const { 
    current, 
    distances = {}, 
    visited = [], 
    queue = [], 
    neighbors_updated = [] 
  } = currentStepData;
  
  // Get the current node being processed
  // Since current is always undefined, let's use available data to show what's happening
  const currentNode = currentStep === 0 ? 'Starting Algorithm' : 
    (neighbors_updated.length > 0 ? `Updating ${neighbors_updated.map(id => getNodeLabel(id)).join(', ')}` :
     queue.length > 0 ? `Processing queue: ${queue.map(item => getNodeLabel(item.node)).join(', ')}` :
     `Step ${currentStep + 1}`);
  
  // Get visited nodes (simplified)
  // Since visited is always undefined, let's infer from distances that are not Infinity
  const visitedNodes = Object.keys(distances)
    .filter(nodeId => {
      const dist = distances[nodeId];
      return dist !== 'Infinity' && dist !== Infinity && dist !== undefined;
    })
    .map(id => getNodeLabel(Number(id)));

  // Get next nodes to visit (from queue)
  const nextNodes = queue.slice(0, 3).map((item) => ({
    node: getNodeLabel(item.node),
    distance: formatDistance(item.distance)
  }));
  
  // Get recent updates (simplified)
  // neighbors_updated is just an array of node IDs, not objects
  const recentUpdates = neighbors_updated.slice(0, 2).map((nodeId) => ({
    node: getNodeLabel(nodeId),
    oldDistance: '∞', // We don't have old distance info
    newDistance: formatDistance(distances[nodeId]) // Get current distance
  }));

  return (
    <div className="step-visualization-content">
      {/* Simple Progress Bar */}
      <div className="progress-section">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          ></div>
        </div>
        <div className="progress-text">
          Step {currentStep + 1} of {totalSteps}
        </div>
      </div>
      
      {/* Simple Navigation */}
      <div className="simple-navigation">
        <button 
          onClick={() => onStepChange?.(currentStep - 1)}
          disabled={currentStep <= 0}
          className="nav-btn prev-btn"
        >
          ← Previous
        </button>
        <button 
          onClick={() => onStepChange?.(currentStep + 1)}
          disabled={currentStep >= totalSteps - 1}
          className="nav-btn next-btn"
        >
          Next →
        </button>
      </div>
      
      {/* What's Happening Now - Simplified */}
      <div className="whats-happening">
        <h3>🔍 What's Happening Now</h3>
        <div className="current-action">
          <div className="action-icon">📍</div>
          <div className="action-text">
            <strong>Looking at node:</strong> {currentNode}
          </div>
        </div>
        
        {visitedNodes.length > 0 && (
          <div className="visited-info">
            <div className="info-icon">✅</div>
            <div className="info-text">
              <strong>Already visited:</strong> {visitedNodes.join(', ')}
            </div>
          </div>
        )}
      </div>
      
      {/* Next Steps - Simplified */}
      {nextNodes.length > 0 && (
        <div className="next-steps">
          <h3>⏭️ Next Steps</h3>
          <div className="next-nodes">
            {nextNodes.map((node, index) => (
              <div key={index} className="next-node">
                <span className="node-name">{node.node}</span>
                <span className="node-distance">({node.distance})</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Recent Updates - Simplified */}
      {recentUpdates.length > 0 && (
        <div className="recent-updates">
          <h3>🔄 Recent Changes</h3>
          <div className="updates-list">
            {recentUpdates.map((update, index) => (
              <div key={index} className="update-item">
                <span className="update-node">{update.node}</span>
                <span className="update-arrow">→</span>
                <span className="update-distance">{update.newDistance}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* All Node Distances - Simplified Grid */}
      <div className="all-distances">
        <h3>All Node Distances</h3>
        <div className="distances-simple">
          {Object.entries(distances).map(([nodeId, distance]) => (
            <div 
              key={nodeId} 
              className={`distance-card ${
                Number(nodeId) === current ? 'current' : ''
              } ${
                visited.includes(Number(nodeId)) ? 'visited' : ''
              }`}
              onClick={() => onNodeHighlight?.(Number(nodeId))}
            >
              <div className="card-node">{getNodeLabel(Number(nodeId))}</div>
              <div className="card-distance">{formatDistance(distance)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PathDisplay;
